import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApplyTemplate } from '@/entities/template';
import { api, queryKeys } from '@/shared/api';
import { Button, Icons } from '@/shared/ui';
import { useQueryClient } from '@tanstack/react-query';

import { TemplatePickerDialog } from './TemplatePickerDialog';
import styles from './Templates.module.css';

const RESOURCE_PATHS = {
  board: 'boards',
  database: 'databases',
  document: 'documents',
} as const;

/**
 * A quiet "From template…" link in a creation dialog. Choosing a template creates the resource in
 * the dialog's destination (workspace, section, group) through the server, then hands the new
 * resource to `onCreated` exactly as a normal creation would. Plain creation stays the default
 * path: nothing here is shown until asked for.
 */
// `T` is the shape the server returns for this kind (the caller knows it); it types the GET below.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
export function FromTemplateButton<T>({
  kind,
  workspaceId,
  sectionId,
  groupId,
  onCreated,
}: {
  kind: keyof typeof RESOURCE_PATHS;
  workspaceId: string | null | undefined;
  sectionId?: string | null | undefined;
  groupId?: string | null | undefined;
  onCreated: (resource: T) => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const apply = useApplyTemplate();
  const [open, setOpen] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  return (
    <div className={styles.fromTemplate}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={!workspaceId}
        data-testid={`${kind}-from-template`}
        onClick={() => {
          setFailure(null);
          setOpen(true);
        }}
      >
        <Icons.layers />
        {t(`templates.use.${kind}`)}
      </Button>
      {open && workspaceId && (
        <TemplatePickerDialog
          kind={kind}
          pending={apply.isPending}
          error={failure}
          onClose={() => {
            setOpen(false);
          }}
          onPick={(template) => {
            apply.mutate(
              { kind, templateId: template.id, workspaceId, sectionId, groupId },
              {
                onSuccess: (made) => {
                  void (async () => {
                    try {
                      const resource = await api.get<T>(
                        `/v1/${RESOURCE_PATHS[kind]}/${made.resourceId}`,
                      );
                      // lists of every kind that may show the new resource
                      await Promise.all(
                        [
                          queryKeys.boards.all,
                          queryKeys.documents.all,
                          queryKeys.databases.mine,
                          queryKeys.workspaces.all,
                        ].map((queryKey) => client.invalidateQueries({ queryKey })),
                      );
                      setOpen(false);
                      onCreated(resource);
                    } catch (error) {
                      setFailure(error);
                    }
                  })();
                },
                onError: setFailure,
              },
            );
          }}
        />
      )}
    </div>
  );
}
